import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiBell } from 'react-icons/bi';
import { useNotifications } from '@/notifications/NotificationsContext';
import { NotificationsPanel } from '@/components/layout/NotificationsPanel';
import './NotificationsBell.css';

/**
 * The live notifications bell (T115) — replaces the decorative `BiBell` button that used to sit
 * inline in `Topbar.tsx`. Owns the badge (unread count, capped display "9+" per DEC-NOT5) + the
 * pulse-once arrival animation, the panel's open/close state, and the always-mounted `aria-live`
 * region for screen-reader arrival announcements (`16-notifications-ux.md` §1/§6).
 */
export function NotificationsBell() {
  const { t } = useTranslation();
  const { unreadCount, arrivalPulse, announcement, setPanelOpen } = useNotifications();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  function toggleOpen() {
    const next = !open;
    setOpen(next);
    setPanelOpen(next);
  }

  function handleClose() {
    setOpen(false);
    setPanelOpen(false);
  }

  const displayCount = unreadCount > 9 ? '9+' : String(unreadCount);
  const ariaLabel =
    unreadCount > 0
      ? t('notifications.bellAriaLabelUnread', { count: unreadCount })
      : t('topbar.notifications');

  return (
    <div className="fa-notifications-bell">
      <button
        type="button"
        ref={triggerRef}
        className="fa-topbar__icon-btn fa-topbar__icon-btn--notifications"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={t('topbar.notifications')}
        onClick={toggleOpen}
      >
        <BiBell aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            key={arrivalPulse ?? 'initial'}
            className={`fa-icon-btn__badge${arrivalPulse ? ' fa-icon-btn__badge--pulse' : ''}`}
            dir="ltr"
            aria-hidden="true"
          >
            {displayCount}
          </span>
        )}
      </button>

      <NotificationsPanel open={open} onClose={handleClose} triggerRef={triggerRef} />

      {/* Separate DOM node from the panel/badge — announces every live arrival to screen-reader
          users regardless of whether the panel is open, §6 "Live region for arrivals". */}
      <span role="status" aria-live="polite" className="fa-visually-hidden">
        {announcement}
      </span>
    </div>
  );
}
